import React, { useCallback, useMemo, useState } from "react";
import cn from "classnames";
import { Brush } from "lucide-react";
import Tooltip from "@material-ui/core/Tooltip";
import DesignDropdownMenu from "./components/DesignDropdownMenu";
import { Design } from "./useDesignManagement";
import { isADMFormat, isEDDMFormat, formatUSPSFormatDisplay } from "utils/formats";

interface DesignCardProps {
  title: string;
  date: string;
  isUpdated?: boolean;
  hasMultiple?: boolean;
  image: string;
  onClick: () => void;
  isActive: boolean;
  design: Design;
  onPreview: (design: Design) => void;
  onDownload?: (design: Design) => void;
  onEdit?: (design: Design) => void;
  isDisabled?: boolean;
}

const DISABLED_TOOLTIP_MESSAGE = "Artwork not compliant with the channel selected";

const DesignCard: React.FC<DesignCardProps> = ({
  title,
  date,
  isUpdated = false,
  hasMultiple = false,
  image,
  onClick,
  isActive,
  design,
  onPreview,
  onDownload,
  onEdit,
  isDisabled = false,
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });

  const virtualAnchor = useMemo(
    () => ({
      getBoundingClientRect: () => ({
        width: 0,
        height: 0,
        top: cursorPosition.y,
        bottom: cursorPosition.y,
        left: cursorPosition.x,
        right: cursorPosition.x,
      }),
    }),
    [cursorPosition]
  );

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isDisabled) {
        return;
      }
      setTooltipOpen(true);
      setCursorPosition({ x: event.clientX, y: event.clientY });
    },
    [isDisabled]
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isDisabled) {
        return;
      }
      const { clientX, clientY } = event;
      setCursorPosition((prev) => {
        if (prev.x === clientX && prev.y === clientY) {
          return prev;
        }
        return { x: clientX, y: clientY };
      });
    },
    [isDisabled]
  );

  const handleMouseLeave = useCallback(() => {
    if (!isDisabled) {
      return;
    }
    setTooltipOpen(false);
  }, [isDisabled]);

  return (
    <Tooltip
      title={DISABLED_TOOLTIP_MESSAGE}
      placement="top"
      enterDelay={0}
      leaveDelay={0}
      enterTouchDelay={0}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      open={isDisabled && tooltipOpen}
      onClose={() => setTooltipOpen(false)}
      PopperProps={{
        anchorEl: isDisabled ? (virtualAnchor as any) : undefined,
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [0, 12],
            },
          },
          {
            name: "flip",
            options: {
              fallbackPlacements: ["bottom"],
            },
          },
        ],
      }}
    >
      <div
        // Anchor per artwork so Copilot control mode can pick one by id rather
        // than by position in a grid that reorders. `aria-selected` and the
        // disabled flag are what tell it which card is already chosen and which
        // ones the channel will refuse, so it never clicks a dead card.
        data-cy={`designCard-${design?.id}`}
        role="option"
        aria-selected={isActive}
        aria-disabled={isDisabled}
        onClick={!isDisabled ? onClick : undefined}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className={cn(
          "flex flex-col gap-1 p-3 bg-white border-light-blue rounded-lg w-80 transition-all duration-200 ease-in-out will-change-[box-shadow,border-color]",
          isActive && !isDisabled && "border-deep-blue shadow-lg",
          !isDisabled && "cursor-pointer hover:shadow-lg hover:shadow-blue-900/20 hover:border-secondary-deep-blue",
          isDisabled && "opacity-40 grayscale select-none"
        )}
      >
        <div className="relative h-40 bg-light-blue rounded p-0.5">
          {image ? (
            <img src={image} alt={title} className="w-full h-full object-cover border-light-blue" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <p className="text-blue-light text-sm font-bold">No preview available</p>
            </div>
          )}
          {hasMultiple && (
            <div className="absolute top-2 right-2 flex gap-0.5">
              <div className="w-3 h-3 bg-white rounded-sm border shadow-sm" />
              <div className="w-3 h-3 bg-white rounded-sm border shadow-sm -ml-1" />
            </div>
          )}
          {(isADMFormat(design.format) || isEDDMFormat(design.format)) && design.addressBox !== true && (
            <div
              className="absolute bottom-2 left-2 px-2 py-0.5 text-white text-xs font-medium rounded shadow-sm"
              style={{ backgroundColor: "#ee4360" }}
            >
              No address box
            </div>
          )}
        </div>
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {isUpdated && <Brush className="w-4 h-4 text-blue-light flex-shrink-0" />}
            <span className="text-blue-deep-dark text-sm font-semibold truncate">{title}</span>
          </div>
          {!isDisabled && (
            <DesignDropdownMenu design={design} onPreview={onPreview} onDownload={onDownload} onEdit={onEdit} />
          )}
        </div>
        <span className="text-blue-light text-xs">Format: {formatUSPSFormatDisplay(design.format)}</span>
        {date && (
          <span className="text-blue-light text-xs">
            {isUpdated ? "Updated" : "Uploaded"} at {date}
          </span>
        )}
      </div>
    </Tooltip>
  );
};

export default DesignCard;
